import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeftIcon, PlusIcon, CopyIcon, TrashIcon, KeyIcon, ShieldSlashIcon,
  UsersThreeIcon, LinkIcon, CheckCircleIcon, ClipboardTextIcon, SealCheckIcon,
  WarningIcon, PencilSimpleIcon, CurrencyDollarIcon, ClockIcon, FilePdfIcon, PulseIcon,
} from "@phosphor-icons/react";

const inputCls = "h-9 rounded-sm bg-zinc-900 border-zinc-800 font-mono text-sm text-zinc-100 focus-visible:ring-1 focus-visible:ring-cyan-500";

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [tempCred, setTempCred] = useState(null); // {email, temp_password}
  const [audit, setAudit] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [psdoc, setPsdoc] = useState(null);

  const loadPsdoc = () => api.get("/admin/psdoc-sync").then(({ data }) => setPsdoc(data)).catch(() => {});

  const load = async () => {
    try {
      const [u, i, a, c] = await Promise.all([
        api.get("/admin/users"), api.get("/admin/invites"),
        api.get("/admin/audit"), api.get("/admin/change-orders"),
      ]);
      setUsers(u.data); setInvites(i.data); setAudit(a.data); setChangeOrders(c.data);
      loadPsdoc();
    } catch (e) { toast.error("Failed to load admin data"); }
  };
  useEffect(() => { load(); }, []);

  const toggleBilled = async (id) => {
    try {
      const { data } = await api.patch(`/admin/change-orders/${id}`);
      setChangeOrders((s) => s.map((c) => (c.id === id ? { ...c, billed: data.billed, status: data.billed ? "billed" : "open" } : c)));
    } catch { toast.error("Failed to update change order"); }
  };

  const downloadCoPdf = async (c) => {
    try {
      const res = await api.get(`/admin/change-orders/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${c.change_code || "change-order"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to generate PDF"); }
  };

  const createInvite = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/invites", { email: inviteEmail || null, role: inviteRole });
      setInviteEmail("");
      const link = `${window.location.origin}/invite/${data.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success("Invite created — link copied to clipboard");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail, "Could not create invite")); }
  };

  const copyLink = async (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    await navigator.clipboard.writeText(link);
    toast.success("Invite link copied");
  };

  const revokeInvite = async (id) => {
    await api.delete(`/admin/invites/${id}`);
    setInvites((s) => s.filter((x) => x.id !== id));
    toast.success("Invite revoked");
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempCred({ email: data.email, temp_password: data.temp_password });
    } catch (err) { toast.error(apiError(err.response?.data?.detail, "Reset failed")); }
  };

  const reset2fa = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/reset-2fa`);
      toast.success(`2FA reset for ${u.email} — they'll re-enroll on next login`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail, "Reset failed")); }
  };

  const deleteUser = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((s) => s.filter((x) => x.id !== u.id));
      toast.success("User deleted");
    } catch (err) { toast.error(apiError(err.response?.data?.detail, "Delete failed")); }
  };

  const inviteStatus = (i) => {
    if (i.used) return { label: "used", cls: "text-zinc-500 border-zinc-700" };
    if (new Date(i.expires_at) < new Date()) return { label: "expired", cls: "text-rose-400 border-rose-500/40" };
    return { label: "active", cls: "text-emerald-400 border-emerald-500/40" };
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="h-14 border-b border-zinc-800 flex items-center justify-between px-4 bg-zinc-950/80 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <button data-testid="admin-back" onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-100 transition-colors">
            <ArrowLeftIcon className="h-4 w-4" /> Back
          </button>
          <div className="h-5 w-px bg-zinc-800" />
          <img src="/soverenx-logo.jpg" alt="SoverenX" className="h-7 object-contain" />
          <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-600">Admin</span>
        </div>
        <span className="text-xs text-zinc-500 font-mono">{user?.email}</span>
      </header>

      <div className="max-w-4xl mx-auto p-6">
        <Tabs defaultValue="users">
          <TabsList className="bg-zinc-900 border border-zinc-800 rounded-md">
            <TabsTrigger data-testid="admin-tab-users" value="users" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-zinc-950 rounded-sm text-sm">
              <UsersThreeIcon className="mr-1.5 h-4 w-4" /> Users
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-invites" value="invites" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-zinc-950 rounded-sm text-sm">
              <LinkIcon className="mr-1.5 h-4 w-4" /> Invites
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-audit" value="audit" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-zinc-950 rounded-sm text-sm">
              <ClipboardTextIcon className="mr-1.5 h-4 w-4" /> Audit
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-changeorders" value="changeorders" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-zinc-950 rounded-sm text-sm">
              <CurrencyDollarIcon className="mr-1.5 h-4 w-4" /> Change Orders
            </TabsTrigger>
            <TabsTrigger data-testid="admin-tab-psdoc" value="psdoc" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-zinc-950 rounded-sm text-sm">
              <PulseIcon className="mr-1.5 h-4 w-4" /> PSDoc Sync
            </TabsTrigger>
          </TabsList>

          {/* USERS */}
          <TabsContent value="users" className="mt-6 space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.id}`}
                className="flex items-center justify-between rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-zinc-100 truncate">{u.name || u.email}</span>
                    <span className={`text-[10px] uppercase rounded-sm border px-1.5 py-0.5 ${u.role === "admin" ? "text-amber-400 border-amber-500/40 bg-amber-500/10" : "text-zinc-400 border-zinc-700"}`}>{u.role}</span>
                    <span className={`text-[10px] rounded-sm border px-1.5 py-0.5 ${u.totp_enabled ? "text-emerald-400 border-emerald-500/40" : "text-zinc-500 border-zinc-700"}`}>
                      {u.totp_enabled ? "2FA on" : "2FA pending"}
                    </span>
                    {u.locked && (
                      <span data-testid={`locked-badge-${u.id}`} className="text-[10px] rounded-sm border border-rose-500/50 bg-rose-500/10 px-1.5 py-0.5 text-rose-400">locked</span>
                    )}
                    {u.must_change_password && (
                      <span className="text-[10px] rounded-sm border border-zinc-700 px-1.5 py-0.5 text-zinc-500">must reset pw</span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-600 font-mono">{u.email}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`reset-password-${u.id}`} onClick={() => resetPassword(u)} title="Reset password"
                    className="flex items-center gap-1 rounded-sm border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 px-2 py-1.5 text-xs text-zinc-300 transition-colors">
                    <KeyIcon className="h-3.5 w-3.5" /> Reset Password
                  </button>
                  <button data-testid={`reset-2fa-${u.id}`} onClick={() => reset2fa(u)} title="Reset 2FA"
                    className="flex items-center gap-1 rounded-sm border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 px-2 py-1.5 text-xs text-zinc-300 transition-colors">
                    <ShieldSlashIcon className="h-3.5 w-3.5" /> Reset 2FA
                  </button>
                  {u.id !== user?.id && (
                    <button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u)} title="Delete user"
                      className="rounded-sm border border-zinc-800 bg-zinc-900 hover:bg-rose-500/10 hover:border-rose-500/40 hover:text-rose-400 px-2 py-1.5 text-zinc-400 transition-colors">
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </TabsContent>

          {/* INVITES */}
          <TabsContent value="invites" className="mt-6 space-y-5">
            <form onSubmit={createInvite} className="flex items-end gap-3 rounded-sm border border-zinc-800 bg-zinc-900/50 p-4">
              <div className="flex-1 space-y-1.5">
                <label className="text-xs text-zinc-400">User email (required)</label>
                <Input data-testid="invite-email-input" type="email" required value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)} placeholder="engineer@company.com" className={inputCls} />
              </div>
              <div className="w-32 space-y-1.5">
                <label className="text-xs text-zinc-400">Role</label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200">
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button data-testid="create-invite-btn"
                className="flex items-center gap-1.5 rounded-sm bg-cyan-500 hover:bg-cyan-400 px-3 py-2 text-sm font-semibold text-zinc-950 transition-colors">
                <PlusIcon weight="bold" className="h-4 w-4" /> Generate link
              </button>
            </form>

            <div className="space-y-2">
              {invites.length === 0 && <p className="text-sm text-zinc-600 text-center py-6">No invites yet.</p>}
              {invites.map((i) => {
                const st = inviteStatus(i);
                return (
                  <div key={i.id} data-testid={`invite-${i.id}`}
                    className="flex items-center justify-between rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-100">{i.email || "(open link)"}</span>
                        <span className={`text-[10px] uppercase rounded-sm border px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
                        <span className="text-[10px] uppercase text-zinc-500">{i.role}</span>
                      </div>
                      <p className="text-xs text-zinc-600">expires {new Date(i.expires_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {!i.used && (
                        <button data-testid={`copy-invite-${i.id}`} onClick={() => copyLink(i.token)}
                          className="flex items-center gap-1 rounded-sm border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 px-2 py-1.5 text-xs text-zinc-300 transition-colors">
                          <CopyIcon className="h-3.5 w-3.5" /> Copy link
                        </button>
                      )}
                      <button data-testid={`revoke-invite-${i.id}`} onClick={() => revokeInvite(i.id)}
                        className="rounded-sm border border-zinc-800 bg-zinc-900 hover:bg-rose-500/10 hover:border-rose-500/40 hover:text-rose-400 px-2 py-1.5 text-zinc-400 transition-colors">
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* AUDIT LOG */}
          <TabsContent value="audit" className="mt-6 space-y-2">
            <p className="text-xs text-zinc-500">Every generated configuration, the engineer who produced it, doc-verification status and sign-off record.</p>
            {audit.length === 0 && <p className="text-sm text-zinc-600 text-center py-8">No configurations generated yet.</p>}
            {audit.map((a) => (
              <div key={a.id} data-testid={`audit-row-${a.id}`} className="rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm text-zinc-100">{a.engineer_name || "—"}</span>
                      <span className="font-mono text-[11px] text-zinc-600">{a.engineer_email}</span>
                      {a.project_name && <span className="rounded-sm border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-200">{a.project_name}</span>}
                      {a.project_code && <span className="rounded-sm border border-cyan-500/40 bg-cyan-500/10 px-1.5 py-0.5 font-mono text-[10px] text-cyan-300">{a.project_code}</span>}
                      {a.change_code && <span className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[10px] text-amber-300">{a.change_code}</span>}
                      <span className="rounded-sm border border-zinc-700 px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">v{a.version || "0.1"}</span>
                      {(a.vendors || []).map((v) => (
                        <span key={v} className="rounded-sm border border-zinc-700 px-1.5 py-0.5 text-[10px] uppercase text-zinc-400">{v}</span>
                      ))}
                      {a.is_revision && (
                        <span className="inline-flex items-center gap-1 rounded-sm border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.5 text-[10px] text-cyan-300">
                          <PencilSimpleIcon className="h-3 w-3" /> revision
                        </span>
                      )}
                    </div>
                    {a.customer && <p className="mt-1 text-[11px] text-zinc-500">Client: {a.customer}{a.approval_name ? ` · Approved by ${a.approval_name}` : ""}</p>}
                    <p className="mt-1 text-xs text-zinc-500 truncate">{a.requirement}</p>
                    <p className="mt-1 text-[11px] text-zinc-600">{a.devices_count} device(s) · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    {a.unverified_count > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-300">
                        <WarningIcon weight="fill" className="h-3 w-3" /> {a.unverified_count} unverified
                      </span>
                    ) : (
                      <span className="rounded-sm border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-500">fully doc-verified</span>
                    )}
                    {a.signed_off ? (
                      <span data-testid={`audit-signed-${a.id}`} className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300"
                        title={a.signed_off_at ? new Date(a.signed_off_at).toLocaleString() : ""}>
                        <SealCheckIcon weight="fill" className="h-3 w-3" /> signed off
                      </span>
                    ) : (
                      <span className="rounded-sm border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-500">not signed off</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* CHANGE ORDERS / BILLING */}
          <TabsContent value="changeorders" className="mt-6 space-y-2">
            <p className="text-xs text-zinc-500">Change orders raised against existing projects — for billing. Each is tied to a PS code and an auto-generated change code.</p>
            {changeOrders.length === 0 && <p className="text-sm text-zinc-600 text-center py-8">No change orders raised yet.</p>}
            {changeOrders.map((c) => (
              <div key={c.id} data-testid={`changeorder-row-${c.id}`} className="rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-sm border border-cyan-500/40 bg-cyan-500/10 px-1.5 py-0.5 font-mono text-[10px] text-cyan-300">{c.project_code}</span>
                      <span className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[10px] text-amber-300">{c.change_code}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-zinc-400"><ClockIcon className="h-3 w-3" /> {c.hours ?? "—"} hrs</span>
                      {c.customer && <span className="text-[11px] text-zinc-500">· {c.customer}</span>}
                    </div>
                    <p className="mt-1 text-sm text-zinc-100">{c.brief || "—"}</p>
                    <p className="mt-0.5 text-xs text-zinc-500 line-clamp-2">{c.scope}</p>
                    <p className="mt-1 text-[11px] text-zinc-600">{c.engineer_name} · {c.created_at ? new Date(c.created_at).toLocaleString() : ""}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <button data-testid={`changeorder-pdf-${c.id}`} onClick={() => downloadCoPdf(c)}
                      className="inline-flex items-center gap-1 rounded-sm border border-zinc-700 px-2 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800 transition-colors">
                      <FilePdfIcon className="h-3.5 w-3.5" /> PDF
                    </button>
                    <button data-testid={`changeorder-bill-${c.id}`} onClick={() => toggleBilled(c.id)}
                      className={`rounded-sm border px-2 py-1 text-[11px] transition-colors ${c.billed ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}>
                      {c.billed ? "Billed ✓" : "Mark billed"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* PSDOC SYNC */}
          <TabsContent value="psdoc" className="mt-6 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-zinc-500">Live status of the PSDoc → Config pipeline (auto-generated designs).</p>
              <button data-testid="psdoc-refresh" onClick={loadPsdoc}
                className="rounded-sm border border-zinc-700 px-2 py-1 text-[11px] text-zinc-300 hover:bg-zinc-800">Refresh</button>
            </div>
            {!psdoc ? (
              <p className="text-sm text-zinc-600 text-center py-8">Loading…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="psdoc-status-cards">
                  <StatCard label="Poller" value={psdoc.enabled ? "Enabled" : "Disabled"} tone={psdoc.enabled ? "ok" : "muted"} />
                  <StatCard label="Last poll" value={psdoc.last_poll_status || "—"} tone={psdoc.last_poll_status === "ok" ? "ok" : (psdoc.last_poll_status ? "bad" : "muted")}
                    sub={psdoc.last_poll_at ? new Date(psdoc.last_poll_at).toLocaleTimeString() : ""} />
                  <StatCard label="Pending" value={psdoc.pending_count ?? "—"} tone="muted" />
                  <StatCard label="Jobs" value={Object.values(psdoc.counts || {}).reduce((a, b) => a + b, 0)} tone="muted" />
                </div>
                <p className="text-[11px] text-zinc-600">Endpoint: <span className="font-mono">{psdoc.base_url || "—"}</span> · every {psdoc.poll_seconds || "60"}s · heartbeat {psdoc.last_ping_status ? `${psdoc.last_ping_status}${psdoc.last_ping_at ? ` @ ${new Date(psdoc.last_ping_at).toLocaleTimeString()}` : ""}` : "—"}{psdoc.last_error ? ` · last error: ${psdoc.last_error}` : ""}</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(psdoc.counts || {}).map(([k, v]) => (
                    <span key={k} className="rounded-sm border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-300">{k}: {v}</span>
                  ))}
                </div>
                <div className="space-y-2">
                  {(psdoc.jobs || []).length === 0 && <p className="text-sm text-zinc-600 text-center py-6">No jobs yet.</p>}
                  {(psdoc.jobs || []).map((j) => {
                    const tone = j.status === "complete" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : j.status === "config_generated" ? "border-violet-500/40 bg-violet-500/10 text-violet-300"
                      : j.status === "in_progress" ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                      : "border-cyan-500/40 bg-cyan-500/10 text-cyan-300";
                    return (
                      <div key={j.ps_code} data-testid={`psdoc-job-${j.ps_code}`} className="rounded-sm border border-zinc-800 bg-zinc-900/50 px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm text-cyan-300">{j.ps_code}</span>
                          <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] ${tone}`}>{j.status}</span>
                          {j.client && <span className="text-[11px] text-zinc-500">· {j.client}</span>}
                          {j.claimed_by && <span className="text-[11px] text-zinc-600">· {j.claimed_by}</span>}
                          <span className="ml-auto text-[10px] text-zinc-600">{j.updated_at ? new Date(j.updated_at).toLocaleString() : ""}</span>
                        </div>
                        {j.psdoc_pushed && <p className="mt-1 text-[11px] text-zinc-600">Last sent to PSDoc: {j.psdoc_pushed}</p>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* temp password dialog */}
      <Dialog open={!!tempCred} onOpenChange={(o) => !o && setTempCred(null)}>
        <DialogContent data-testid="temp-password-dialog" className="max-w-md bg-zinc-950 border-zinc-800 text-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-emerald-400" /> Temporary password created
            </DialogTitle>
            <DialogDescription className="text-zinc-500">One-time password for the selected user. 2FA is still required at sign-in.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-zinc-400">Share this one-time password with <span className="text-zinc-200">{tempCred?.email}</span>. They'll use it to sign in (2FA still required).</p>
          <div className="flex items-center gap-2">
            <code data-testid="temp-password-value" className="flex-1 rounded-sm bg-zinc-900 border border-zinc-800 px-3 py-2 text-cyan-300 text-sm font-mono break-all">{tempCred?.temp_password}</code>
            <button onClick={() => { navigator.clipboard.writeText(tempCred.temp_password); toast.success("Copied"); }}
              className="rounded-sm bg-cyan-500 hover:bg-cyan-400 px-3 py-2 text-zinc-950 transition-colors">
              <CopyIcon className="h-4 w-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


const StatCard = ({ label, value, sub, tone = "muted" }) => {
  const toneCls = tone === "ok" ? "text-emerald-300" : tone === "bad" ? "text-rose-300" : "text-zinc-100";
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-900/50 px-3 py-2.5">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-sm font-semibold ${toneCls}`}>{value}</p>
      {sub && <p className="text-[10px] text-zinc-600">{sub}</p>}
    </div>
  );
};
